import { useState, useEffect } from 'react';
import { X, User, Phone, MapPin, Calendar, Loader2, Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { PRIVACY_VERSION, TERMS_VERSION } from '@/lib/legalVersions';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultMode?: 'login' | 'signup';
  onLoginSuccess?: (user: any) => void;
}

const nameSchema = z.string().min(2, 'Nome deve ter no mínimo 2 caracteres');
const emailSchema = z.string().email('E-mail inválido');
const passwordSchema = z.string().min(6, 'Senha deve ter no mínimo 6 caracteres');
const phoneSchema = z.string().min(10, 'Telefone deve ter no mínimo 10 dígitos').max(15, 'Telefone inválido');

const ESTADOS_BRASILEIROS = [
  "Acre (AC)", "Alagoas (AL)", "Amapá (AP)", "Amazonas (AM)",
  "Bahia (BA)", "Ceará (CE)", "Distrito Federal (DF)", "Espírito Santo (ES)",
  "Goiás (GO)", "Maranhão (MA)", "Mato Grosso (MT)", "Mato Grosso do Sul (MS)",
  "Minas Gerais (MG)", "Pará (PA)", "Paraíba (PB)", "Paraná (PR)",
  "Pernambuco (PE)", "Piauí (PI)", "Rio de Janeiro (RJ)", "Rio Grande do Norte (RN)",
  "Rio Grande do Sul (RS)", "Rondônia (RO)", "Roraima (RR)", "Santa Catarina (SC)",
  "São Paulo (SP)", "Sergipe (SE)", "Tocantins (TO)",
  "Não moro no Brasil"
];

const FAIXAS_ETARIAS = [
  "18-30 anos",
  "31-40 anos",
  "41-50 anos",
  "51-60 anos",
  "60+ anos"
];

export function AuthModal({ isOpen, onClose, onSuccess, defaultMode, onLoginSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(defaultMode || 'signup');
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [genero, setGenero] = useState<'masculino' | 'feminino' | 'outros' | ''>('');
  const [telefone, setTelefone] = useState('');
  const [localizacao, setLocalizacao] = useState('');
  const [faixaEtaria, setFaixaEtaria] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>();
  const [cooldown, setCooldown] = useState(0);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptAI, setAcceptAI] = useState(false);
  const [isAdult, setIsAdult] = useState(false);

  const { signIn, signUp, resetPassword } = useAuth();

  // Sync mode when defaultMode or isOpen changes
  useEffect(() => {
    if (isOpen && defaultMode) setMode(defaultMode);
  }, [isOpen, defaultMode]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (!isOpen) return null;

  const translateAuthError = (msg: string): { text: string; seconds?: number } => {
    const m = msg.match(/after (\d+) seconds?/i);
    if (m) {
      const s = parseInt(m[1], 10);
      return { text: `Muitas tentativas. Aguarde ${s}s antes de tentar de novo.`, seconds: s };
    }
    if (/email rate limit exceeded/i.test(msg)) {
      return { text: 'Limite de envio de e-mails atingido. Tente novamente em alguns minutos.', seconds: 60 };
    }
    if (/over_email_send_rate_limit/i.test(msg)) {
      return { text: 'Muitos e-mails enviados recentemente. Tente novamente em alguns minutos.', seconds: 60 };
    }
    if (/already registered/i.test(msg)) {
      return { text: 'Este e-mail já está cadastrado. Tente fazer login.' };
    }
    if (/Invalid login credentials/i.test(msg)) {
      return { text: 'E-mail ou senha incorretos.' };
    }
    if (/non-2xx|send_failed|FunctionsHttpError|server_misconfigured/i.test(msg)) {
      return {
        text: 'Não foi possível enviar o e-mail de recuperação agora. O envio de e-mails do domínio está fora do ar — fale com o suporte.',
      };
    }
    return { text: msg };
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 11);
    if (limited.length <= 2) return limited;
    if (limited.length <= 7) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTelefone(formatPhoneNumber(e.target.value));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (mode === 'forgot') {
      const emailResult = emailSchema.safeParse(email);
      if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Email and password for both login and signup
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;
    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) newErrors.password = passwordResult.error.errors[0].message;

    if (mode === 'signup') {
      const nameResult = nameSchema.safeParse(nome);
      if (!nameResult.success) newErrors.nome = nameResult.error.errors[0].message;
      if (!genero) newErrors.genero = 'Selecione seu gênero';
      const telefoneLimpo = telefone.replace(/\D/g, '');
      const phoneResult = phoneSchema.safeParse(telefoneLimpo);
      if (!phoneResult.success) newErrors.telefone = phoneResult.error.errors[0].message;
      if (!localizacao) newErrors.localizacao = 'Selecione sua localização';
      if (!faixaEtaria) newErrors.faixaEtaria = 'Selecione sua faixa etária';
      if (!acceptTerms) newErrors.acceptTerms = 'Você precisa aceitar os Termos e a Política de Privacidade';
      if (!acceptAI) newErrors.acceptAI = 'Necessário para usar as funções de IA';
      if (!isAdult) newErrors.isAdult = 'Você precisa confirmar que tem 18 anos ou mais';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (cooldown > 0) return;

    setLoading(true);

    try {
      if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          const t = translateAuthError(error.message);
          if (t.seconds) setCooldown(t.seconds);
          toast.error(t.text);
        } else {
          toast.success('Link de recuperação enviado para seu e-mail!');
          setMode('login');
        }
        return;
      }

      if (mode === 'signup') {
        const telefoneLimpo = telefone.replace(/\D/g, '');
        const { error } = await signUp(email, password, nome, genero, telefoneLimpo, localizacao, faixaEtaria);
        if (error) {
          const t = translateAuthError(error.message);
          if (t.seconds) setCooldown(t.seconds);
          toast.error(t.text);
          return;
        }
        // Registra consentimentos como prova legal (LGPD)
        Promise.all([
          supabase.functions.invoke('record-consent', { body: { consent_type: 'terms', version: TERMS_VERSION, accepted: true } }),
          supabase.functions.invoke('record-consent', { body: { consent_type: 'privacy', version: PRIVACY_VERSION, accepted: true } }),
          supabase.functions.invoke('record-consent', { body: { consent_type: 'ai_processing', version: PRIVACY_VERSION, accepted: true } }),
          supabase.functions.invoke('record-consent', { body: { consent_type: 'age_18_plus', version: TERMS_VERSION, accepted: true } }),
        ]).catch(() => {});
        setCooldown(60);
        toast.success('Conta criada! Verifique seu e-mail para confirmar.');
        onSuccess();
      } else {
        const { data, error } = await signIn(email, password);
        if (error) {
          const t = translateAuthError(error.message);
          if (t.seconds) setCooldown(t.seconds);
          toast.error(t.text);
          return;
        }
        toast.success('Login realizado com sucesso!');
        if (onLoginSuccess && data.user) {
          onLoginSuccess(data.user);
        } else {
          onSuccess();
        }
      }
    } catch (err) {
      toast.error('Ocorreu um erro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: 'login' | 'signup' | 'forgot') => {
    setMode(newMode);
    setErrors({});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-spotify-black border border-border rounded-2xl p-5 sm:p-8 w-full max-w-md mx-4 shadow-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-display text-foreground mb-2">
            {mode === 'signup' ? 'Criar Conta' : mode === 'login' ? 'Entrar' : 'Recuperar Senha'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {mode === 'signup' 
              ? 'Crie sua conta para descobrir sua persona de estilo'
              : mode === 'login'
              ? 'Entre com seu e-mail e senha'
              : 'Digite seu e-mail para receber o link de recuperação'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              {/* Nome */}
              <div>
                <label className="text-foreground text-sm mb-2 block">Nome</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={nome}
                    onChange={e => setNome(e.target.value)}
                    className="w-full bg-secondary border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder-muted-foreground focus:border-spotify-green focus:outline-none"
                    placeholder="Seu nome"
                  />
                </div>
                {errors?.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
              </div>

              {/* Gênero */}
              <div>
                <label className="text-foreground text-sm mb-2 block">Gênero</label>
                <div className="flex gap-3">
                  {(['feminino', 'masculino', 'outros'] as const).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGenero(g)}
                      className={`flex-1 py-3 rounded-lg border transition-colors font-medium ${
                        genero === g
                          ? 'bg-spotify-green border-spotify-green text-spotify-black'
                          : 'bg-secondary border-border text-foreground hover:border-spotify-green'
                      }`}
                    >
                      {g === 'feminino' ? 'Feminino' : g === 'masculino' ? 'Masculino' : 'Outros'}
                    </button>
                  ))}
                </div>
                {errors?.genero && <p className="text-red-500 text-xs mt-1">{errors.genero}</p>}
              </div>
            </>
          )}

          {/* E-mail - shown in all modes */}
          <div>
            <label className="text-foreground text-sm mb-2 block">E-mail</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-secondary border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder-muted-foreground focus:border-spotify-green focus:outline-none"
                placeholder="seu@email.com"
              />
            </div>
            {errors?.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Password - login and signup only */}
          {mode !== 'forgot' && (
            <div>
              <label className="text-foreground text-sm mb-2 block">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-lg pl-10 pr-12 py-3 text-foreground placeholder-muted-foreground focus:border-spotify-green focus:outline-none transition-colors"
                  placeholder="Mínimo 6 caracteres"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors?.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>
          )}

          {/* Forgot password link - login mode only */}
          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => switchMode('forgot')}
                className="text-spotify-green hover:underline text-sm"
              >
                Esqueci minha senha
              </button>
            </div>
          )}

          {mode === 'signup' && (
            <>
              {/* Telefone */}
              <div>
                <label className="text-foreground text-sm mb-2 block">Telefone (WhatsApp)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="tel"
                    value={telefone}
                    onChange={handlePhoneChange}
                    className="w-full bg-secondary border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder-muted-foreground focus:border-spotify-green focus:outline-none"
                    placeholder="(00) 00000-0000"
                  />
                </div>
                {errors?.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
              </div>

              {/* Localização */}
              <div>
                <label className="text-foreground text-sm mb-2 block">Localização</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                  <Select value={localizacao} onValueChange={setLocalizacao}>
                    <SelectTrigger className="w-full bg-secondary border-border pl-10 h-12 text-foreground">
                      <SelectValue placeholder="Selecione seu estado..." />
                    </SelectTrigger>
                    <SelectContent className="bg-secondary border-border max-h-60">
                      {ESTADOS_BRASILEIROS.map((estado) => (
                        <SelectItem 
                          key={estado} 
                          value={estado}
                          className="text-foreground hover:bg-spotify-green/20"
                        >
                          {estado}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors?.localizacao && <p className="text-red-500 text-xs mt-1">{errors.localizacao}</p>}
              </div>

              {/* Faixa Etária */}
              <div>
                <label className="text-foreground text-sm mb-2 block">Faixa Etária</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                  <Select value={faixaEtaria} onValueChange={setFaixaEtaria}>
                    <SelectTrigger className="w-full bg-secondary border-border pl-10 h-12 text-foreground">
                      <SelectValue placeholder="Selecione sua faixa etária..." />
                    </SelectTrigger>
                    <SelectContent className="bg-secondary border-border">
                      {FAIXAS_ETARIAS.map((faixa) => (
                        <SelectItem 
                          key={faixa} 
                          value={faixa}
                          className="text-foreground hover:bg-spotify-green/20"
                        >
                          {faixa}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors?.faixaEtaria && <p className="text-red-500 text-xs mt-1">{errors.faixaEtaria}</p>}
              </div>
            </>
          )}

          {mode === 'signup' && (
            <div className="space-y-3 pt-2 border-t border-border/40">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={isAdult}
                  onCheckedChange={(v) => setIsAdult(v === true)}
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Confirmo que tenho 18 anos ou mais.</strong>{' '}
                  Esta plataforma não é destinada a menores de idade.
                </span>
              </label>
              {errors?.isAdult && <p className="text-red-500 text-xs">{errors.isAdult}</p>}

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(v === true)}
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground">
                  Li e concordo com os{' '}
                  <Link to="/termos-de-uso" target="_blank" className="text-primary hover:underline">Termos de Uso</Link>{' '}e a{' '}
                  <Link to="/privacidade" target="_blank" className="text-primary hover:underline">Política de Privacidade</Link>.
                </span>
              </label>
              {errors?.acceptTerms && <p className="text-red-500 text-xs">{errors.acceptTerms}</p>}

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acceptAI}
                  onCheckedChange={(v) => setAcceptAI(v === true)}
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground">
                  Autorizo o processamento das minhas mensagens e fotos por inteligência artificial (OpenAI) para gerar
                  análises e recomendações personalizadas.
                </span>
              </label>
              {errors?.acceptAI && <p className="text-red-500 text-xs">{errors.acceptAI}</p>}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || cooldown > 0}
            className="w-full bg-spotify-green hover:bg-spotify-green-dark text-spotify-black font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processando...
              </>
            ) : cooldown > 0 ? (
              `Aguarde ${cooldown}s...`
            ) : (
              mode === 'signup' ? 'Continuar' : mode === 'login' ? 'Entrar' : 'Enviar Link de Recuperação'
            )}
          </button>
        </form>

        {/* Toggle mode */}
        <div className="mt-4 text-center space-y-2">
          {mode === 'forgot' ? (
            <button
              type="button"
              onClick={() => switchMode('login')}
              className="text-spotify-green hover:underline text-sm inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao login
            </button>
          ) : (
            <p className="text-muted-foreground text-sm">
              {mode === 'signup' ? 'Já tem uma conta?' : 'Não tem uma conta?'}
              <button
                type="button"
                onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}
                className="text-spotify-green hover:underline ml-1 font-medium"
              >
                {mode === 'signup' ? 'Entrar' : 'Criar conta'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
